import { useCallback, useEffect, useRef, useState } from 'react';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import { EMPTY_CONDITION_FIELDS, type ConditionFields } from './reactionConditions';

/**
 * #705 -- what a reaction on the paper carries beyond its structures: the arrow's conditions
 * (the Conditions card) and which molecules above the arrow are catalysts rather than reagents.
 * Held beside the drawing (not in it), like the reaction boxes; reset with the drawing, and filled
 * when a saved reaction is opened (#705 part 2).
 *
 * A catalyst is remembered by its atoms: a molecule counts as a catalyst when any of its atoms is
 * marked, so it stays one while it is moved or edited.
 */
interface DetailsSnapshot {
  readonly fields: ConditionFields;
  readonly catalysts: ReadonlySet<string>;
}

export function useReactionDetails(present: DrawingDocument) {
  const [conditionFields, setConditionFields] = useState<ConditionFields>(EMPTY_CONDITION_FIELDS);
  const [catalystAtomIds, setCatalystAtomIds] = useState<ReadonlySet<string>>(() => new Set());
  // #744 -- the details as they were at particular history entries: placing a reaction remembers
  // them for the entry before and the entry it creates, so undo / redo bring them back with it.
  const snapshots = useRef(new WeakMap<DrawingDocument, DetailsSnapshot>());
  useEffect(() => {
    const snapshot = snapshots.current.get(present);
    if (snapshot) {
      setConditionFields(snapshot.fields);
      setCatalystAtomIds(snapshot.catalysts);
    }
  }, [present]);

  const setConditionField = useCallback((field: keyof ConditionFields, value: string) => {
    setConditionFields((current) => ({ ...current, [field]: value }));
  }, []);

  const isCatalyst = useCallback(
    (atomIds: readonly string[]) => atomIds.some((id) => catalystAtomIds.has(id)),
    [catalystAtomIds]
  );

  /** Marks a molecule (its atoms) as a catalyst, or back to a reagent. */
  const setCatalyst = useCallback((atomIds: readonly string[], catalyst: boolean) => {
    setCatalystAtomIds((current) => {
      const next = new Set(current);
      for (const id of atomIds) {
        if (catalyst) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const resetReactionDetails = useCallback(() => {
    setConditionFields(EMPTY_CONDITION_FIELDS);
    setCatalystAtomIds(new Set());
  }, []);

  /** Fills both from a saved reaction (#705 part 2). */
  const loadReactionDetails = useCallback((fields: ConditionFields, catalysts: Iterable<string>) => {
    setConditionFields(fields);
    setCatalystAtomIds(new Set(catalysts));
  }, []);

  /** #744 -- the details change along with the drawing (`before` -> `after`, one undo step):
   *  both entries remember theirs, and `after`'s apply now. */
  const changeReactionDetailsWith = useCallback(
    (before: DrawingDocument, after: DrawingDocument, fields: ConditionFields, catalysts: Iterable<string>) => {
      snapshots.current.set(before, { fields: conditionFields, catalysts: catalystAtomIds });
      snapshots.current.set(after, { fields, catalysts: new Set(catalysts) });
    },
    [catalystAtomIds, conditionFields]
  );

  return {
    conditionFields,
    /** #1165: kept with an unsaved draft, so a restored reaction keeps its catalysts. */
    catalystAtomIds,
    changeReactionDetailsWith,
    setConditionField,
    isCatalyst,
    setCatalyst,
    resetReactionDetails,
    loadReactionDetails
  };
}
